import React, { useEffect, useRef, useState } from "react";
import { Button, Card, DatePicker, Empty, Input, Popover, Select, Space, Table, Tooltip } from "antd";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { firestore } from "../../../../configs/FirebaseConfig";
import { uniqueMergeArrays } from "functions/arrays";
import utils from "utils";
import dayjs from "dayjs";
import Flex from "components/shared-components/Flex";
import Loading from "components/shared-components/Loading";
import { COMPANY_COUNTRY_CODE } from "../../../../constants/CompanyConstants";
import { PRODUCT_COMPANY_GROUPS, PRODUCT_GROUPS } from "../../../../constants/ProductConstant";
import ProductStockModal from "./ProductStockModal";
import { useStockData } from "../../../../data/hooks/firestore/useStock";
import { isMobile } from "../../../../functions/browser";
import {
  FIRESTORE_DEFAULT_PROJECTIONS_DOC,
  FIRESTORE_PRODUCTS_STOCK_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../../../../constants/FirestoreConstant";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { getPackingDateId } from "../../../../functions/calculations";
import { useOrders } from "../../../../data/hooks/firestore/useOrders";
import { useProducts } from "../../../../data/hooks/firestore/useProducts";
import styled from "styled-components";
import { getUserPermissions } from "../../../../utils/permissionUtils";
import roles from "../../../../roles";
import { useAuth } from "../../../../data/hooks/firebase/useAuth";
import { StyledSelect } from "../../../../styles/sharedStyles";
import useIsMobile from "../../../../hooks/useIsMobile";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
const { Option } = Select;
const { RangePicker } = DatePicker;

const Forecast = () => {
  const { t } = useTranslation();
  const { isLoading, stockDates, stockData } = useStockData();
  const { products, isLoading: productsLoading } = useProducts();
  const [productList, setProductList] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const { data: orders } = useOrders(null, dateRange);
  const [ordersInDateRange, setOrdersInDateRange] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const categories = uniqueMergeArrays(products, "productCategoryNL");
  const [clickedCategory, setClickedCategory] = useState(localStorage.getItem("categoryFilter") || "All");
  const [clickedProductCompany, setClickedProductCompany] = useState(
    localStorage.getItem("productCompanyFilter") || "All",
  );
  const [clickedProductGroup, setClickedProductGroup] = useState(localStorage.getItem("productGroupFilter") || "All");
  const [activeTimeFilter, setActiveTimeFilter] = useState("week");
  const [customDatePickerVisible, setCustomDatePickerVisible] = useState(false);
  const [tempDateRange, setTempDateRange] = useState(null);
  const isMounted = useRef(false);

  // State for tracking additional date fetching
  const [needsAdditionalFetch, setNeedsAdditionalFetch] = useState(false);
  const [additionalDateRange, setAdditionalDateRange] = useState(null);
  const [additionalFetchKey, setAdditionalFetchKey] = useState("");
  const [additionalStockDataLocal, setAdditionalStockData] = useState([]);
  const additionalStockData = additionalStockDataLocal;

  // Dynamic table height state
  const tableContainerRef = useRef(null);
  const [tableY, setTableY] = useState(600);

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);
  const isMobileView = useIsMobile();

  // Permissions
  const { role: currentUserRole, uid } = useAuth();
  const [userPermissions, setUserPermissions] = useState({});

  useEffect(() => {
    const loadPermissions = async () => {
      if (currentUserRole === roles.superAdmin) {
        setUserPermissions({ edit: true });
      } else if (currentUserRole === roles.admin) {
        const permissions = await getUserPermissions(uid, currentUserRole);
        setUserPermissions(permissions["shop-products-forecast"] || { view: false, edit: false });
      }
    };
    loadPermissions();
  }, [currentUserRole, uid]);

  const hasEditPermission = currentUserRole === roles.superAdmin || userPermissions.edit === true;

  useEffect(() => {
    let unsubscribe = null;

    if (needsAdditionalFetch && additionalDateRange && additionalFetchKey) {
      const startUnix = dayjs(additionalDateRange[0]).startOf("day").unix();
      const endUnix = dayjs(additionalDateRange[1]).endOf("day").unix();

      // Manual Firestore listener
      const stockQuery = query(
        collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_PRODUCTS_STOCK_TABLE),
        where("dateUnix", ">=", startUnix),
        where("dateUnix", "<=", endUnix),
        orderBy("dateUnix", "asc"),
      );
      unsubscribe = onSnapshot(
        stockQuery,
        (snapshot) => {
          setAdditionalStockData(snapshot.docs);
        },
        (error) => {
          console.error("Error in additional data listener:", error);
        },
      );
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [needsAdditionalFetch, additionalDateRange, additionalFetchKey, firestore]);

  // Check if date range needs additional fetching
  const checkIfNeedsAdditionalFetch = (selectedDateRange) => {
    if (!selectedDateRange || selectedDateRange.length !== 2) {
      setNeedsAdditionalFetch(false);
      setAdditionalDateRange(null);
      setAdditionalFetchKey("");
      return false;
    }

    const selectedStart = dayjs(selectedDateRange[0]).startOf("day");
    const selectedEnd = dayjs(selectedDateRange[1]).endOf("day");

    // Get context date range (28 days past to 28 days future)
    const contextStart = dayjs().subtract(28, "days").startOf("day");
    const contextEnd = dayjs().add(28, "days").endOf("day");

    // Check if selected range extends beyond context range
    const needsFetch = selectedStart.isBefore(contextStart) || selectedEnd.isAfter(contextEnd);

    if (needsFetch) {
      // Create a unique key to avoid conflicts
      const fetchKey = `${selectedStart.format("YYYYMMDD")}_${selectedEnd.format("YYYYMMDD")}`;
      setNeedsAdditionalFetch(true);
      setAdditionalDateRange(selectedDateRange);
      setAdditionalFetchKey(fetchKey);
      return true;
    } else {
      setNeedsAdditionalFetch(false);
      setAdditionalDateRange(null);
      setAdditionalFetchKey("");
      return false;
    }
  };

  // Filter orders based on selected date range
  const filterOrdersByDateRange = (dateRange) => {
    if (!orders || !dateRange || dateRange.length !== 2) {
      setOrdersInDateRange([]);
      return;
    }

    const startDate = dayjs(dateRange[0]).startOf("day");
    const endDate = dayjs(dateRange[1]).endOf("day");

    const { allDates } = getAllAvailableData();

    // Get all date IDs in the selected range
    const dateIdsInRange = allDates
      .filter((date) => {
        const stockDate = dayjs(date.dateUnix * 1000);
        return stockDate.isSameOrAfter(startDate, "day") && stockDate.isSameOrBefore(endDate, "day");
      })
      .map((date) => date.id);

    // Filter orders that have packing dates matching the date IDs in range
    const filteredOrders = orders
      .filter((order) => {
        if (!order.farmorderDeliveryDate) return false;

        const packingDateId = getPackingDateId(order.farmorderDeliveryDate);
        return dateIdsInRange.includes(packingDateId);
      })
      .map((order) => ({
        ...order,
        packingDateId: getPackingDateId(order.farmorderDeliveryDate),
      }));

    setOrdersInDateRange(filteredOrders);
  };

  // Get all available dates and data (but keep sources separate)
  const getAllAvailableData = () => {
    let allDates = [...stockDates];

    // Add additional dates if available (but don't combine the data)
    if (additionalStockData && additionalStockData.length > 0) {
      additionalStockData.forEach((doc) => {
        // Check if it's a Firestore document or already processed data
        const data = typeof doc.data === "function" ? doc.data() : doc;
        const docId = doc.id || data.id;

        if (docId !== FIRESTORE_DEFAULT_PROJECTIONS_DOC) {
          // Check if this date is not already in context data
          const existingDate = allDates.find((d) => d.id === docId);
          if (!existingDate) {
            allDates.push({
              id: docId,
              date: data.date,
              dateUnix: data.dateUnix,
              formattedDate: dayjs(data.dateUnix * 1000).format("DD-MM-YYYY"),
            });
          }
        }
      });

      // Sort all dates
      allDates.sort((a, b) => a.dateUnix - b.dateUnix);
    }
    return { allDates };
  };

  // Get stock data for a specific date (check both sources)
  const getStockDataForDate = (dateId, productId) => {
    // First check context data
    if (stockData[dateId] && stockData[dateId][productId]) {
      return stockData[dateId][productId];
    }

    // Then check additional data
    if (additionalStockData && additionalStockData.length > 0) {
      const additionalDoc = additionalStockData.find((doc) => {
        const docId = doc.id || (typeof doc.data === "function" ? doc.id : doc.id);
        return docId === dateId;
      });

      if (additionalDoc) {
        const data = typeof additionalDoc.data === "function" ? additionalDoc.data() : additionalDoc;
        return (
          data.products?.[productId] || {
            projectedStock: 0,
            countedStock: 0,
            requestedStock: 0,
            currentStock: 0,
          }
        );
      }
    }

    // Default fallback
    return {
      projectedStock: 0,
      countedStock: 0,
      requestedStock: 0,
      currentStock: 0,
    };
  };

  // First load of data - transform the product data
  useEffect(() => {
    if (products && products.length > 0) {
      const { allDates } = getAllAvailableData();

      if (allDates.length > 0) {
        transformProductData();

        // If we have data and no date range set, apply the week filter
        if (!dateRange) {
          setTimeout(() => {
            applyTimeFilter("week");
          }, 100);
        }
      }
    }
  }, [products, stockData, stockDates, additionalStockData]);

  useEffect(() => {
    isMounted.current = true;
    // Compute dynamic table height on mount and when resizing
    const updateTableHeight = () => {
      try {
        if (tableContainerRef.current) {
          const rect = tableContainerRef.current.getBoundingClientRect();
          // Leave a small bottom margin to avoid overlapping footer
          const bottomOffset = 24; // px
          const computed = window.innerHeight - rect.top - bottomOffset;
          setTableY(Math.max(200, Math.floor(computed))); // enforce a sensible minimum
        }
      } catch (e) {
        // Fallback to default on any error
        setTableY(600);
      }
    };
    updateTableHeight();
    window.addEventListener("resize", updateTableHeight);
    return () => {
      isMounted.current = false;
      window.removeEventListener("resize", updateTableHeight);
    };
  }, []);

  useEffect(() => {
    if (!isLoading && products?.length > 0 && productList?.length > 0) {
      setTimeout(() => {
        applyTimeFilter("week");
      }, 300);
    }
  }, [isLoading, productsLoading]);

  const transformProductData = () => {
    const { allDates } = getAllAvailableData();

    const transformedProducts = products.map((product) => {
      const productData = {
        key: product.id,
        productId: product.productId,
        productName: product.productName || product.productNameNL,
        productCategory: product.productCategory || product.productCategoryNL,
        productCompany: product.company,
        productGroup: product.productGroup,
      };

      // Add stock data for each date (using separate data sources)
      allDates.forEach((date) => {
        const productStock = getStockDataForDate(date.id, product.id);
        productData[`${date.id}_projected`] = productStock.projectedStock;
        productData[`${date.id}_counted`] = productStock.countedStock;
        productData[`${date.id}_requested`] = productStock.requestedStock;
        productData[`${date.id}_current`] = productStock.currentStock;
      });

      return productData;
    });

    setProductList(transformedProducts);

    // If we already have filters, apply them
    if (
      searchText ||
      clickedCategory !== "All" ||
      clickedProductCompany !== "All" ||
      clickedProductGroup !== "All" ||
      dateRange
    ) {
      applyFilters(
        transformedProducts,
        searchText,
        clickedCategory,
        clickedProductCompany,
        clickedProductGroup,
        dateRange,
      );
    } else {
      setFilteredProducts(transformedProducts);
    }
  };

  // Filters and search
  const onSearch = (value) => {
    setSearchText(value);
    applyFilters(productList, value, clickedCategory, clickedProductCompany, clickedProductGroup, dateRange);
  };

  const handleShowCategory = (value) => {
    localStorage.setItem("categoryFilter", value);
    setClickedCategory(value);
    applyFilters(productList, searchText, value, clickedProductCompany, clickedProductGroup, dateRange);
  };

  const handleShowProductCompany = (value) => {
    localStorage.setItem("productCompanyFilter", value);
    setClickedProductCompany(value);
    applyFilters(productList, searchText, clickedCategory, value, clickedProductGroup, dateRange);
  };

  const handleShowProductGroup = (value) => {
    localStorage.setItem("productGroupFilter", value);
    setClickedProductGroup(value);
    applyFilters(productList, searchText, clickedCategory, clickedProductCompany, value, dateRange);
  };

  const handleDateRangeChange = (dates) => {
    // Only process if we have both start and end dates
    if (dates && dates.length === 2 && dates[0] && dates[1]) {
      setDateRange(dates);
      setActiveTimeFilter("custom");

      // Check if we need additional fetching
      checkIfNeedsAdditionalFetch(dates);
      applyFilters(productList, searchText, clickedCategory, clickedProductCompany, clickedProductGroup, dates);

      // Filter orders based on the new date range
      filterOrdersByDateRange(dates);
    }
  };

  // Apply time filter (week, month)
  const applyTimeFilter = (filter) => {
    const today = dayjs().startOf("day");
    let endDate;

    if (filter === "week") {
      endDate = dayjs().add(6, "days").endOf("day");
    } else if (filter === "month") {
      endDate = dayjs().add(27, "days").endOf("day");
    } else {
      return;
    }

    const newDateRange = [today, endDate];

    setDateRange(newDateRange);
    setActiveTimeFilter(filter);

    // Check if we need additional fetching (likely not for week/month within 28 days)
    checkIfNeedsAdditionalFetch(newDateRange);

    if (productList && productList.length > 0) {
      const filtered = applyFiltersDirectly(
        productList,
        searchText,
        clickedCategory,
        clickedProductCompany,
        newDateRange,
        clickedProductGroup,
      );
      setFilteredProducts(filtered);
    }

    // Filter orders based on the new date range
    filterOrdersByDateRange(newDateRange);
  };

  // Direct version that returns the filtered array instead of setting state
  const applyFiltersDirectly = (
    products,
    searchValue = searchText,
    category = clickedCategory,
    productCompany = clickedProductCompany,
    dateRange,
    productGroup = clickedProductGroup,
  ) => {
    const { allDates } = getAllAvailableData();
    let filtered = [...products];

    // Apply category filter
    if (category && category !== "All") {
      filtered = filtered.filter((product) => product.productCategory === category);
    }

    // Apply company filter
    if (productCompany && productCompany !== "All") {
      filtered = filtered.filter((product) => product.productCompany === productCompany);
    }

    // Apply product group filter
    if (productGroup && productGroup !== "All") {
      filtered = filtered.filter((product) => product.productGroup === productGroup);
    }

    // Apply search filter
    if (searchValue) {
      filtered = filtered.filter(
        (product) =>
          product.productId.toLowerCase().includes(searchValue.toLowerCase()) ||
          (product.productName && product.productName.toLowerCase().includes(searchValue.toLowerCase())),
      );
    }

    // Apply date range filter
    if (dateRange && dateRange.length === 2) {
      const startDate = dayjs(dateRange[0]);
      const endDate = dayjs(dateRange[1]);

      // Find matching dates from all available dates that fall within range
      const matchingDates = allDates.filter((d) => {
        const stockDate = d.dateUnix * 1000;
        return dayjs(stockDate).isSameOrAfter(startDate, "day") && dayjs(stockDate).isSameOrBefore(endDate, "day");
      });

      if (matchingDates.length > 0) {
        // Create product objects with only the columns we need
        filtered = filtered.map((product) => {
          const filteredProduct = {
            key: product.key,
            productId: product.productId,
            productName: product.productName,
            productCategory: product.productCategory,
            productCompany: product.productCompany,
            productGroup: product.productGroup,
          };

          // Add columns for each date in range
          matchingDates.forEach((matchDate) => {
            filteredProduct[`${matchDate.id}_projected`] = product[`${matchDate.id}_projected`];
            filteredProduct[`${matchDate.id}_counted`] = product[`${matchDate.id}_counted`];
            filteredProduct[`${matchDate.id}_requested`] = product[`${matchDate.id}_requested`];
            filteredProduct[`${matchDate.id}_current`] = product[`${matchDate.id}_current`];
          });

          return filteredProduct;
        });
      }
    }

    return filtered;
  };

  const applyFilters = (
    products,
    searchValue = searchText,
    category = clickedCategory,
    productCompany = clickedProductCompany,
    productGroup = clickedProductGroup,
    dateRange = dateRange,
  ) => {
    const filtered = applyFiltersDirectly(products, searchValue, category, productCompany, dateRange, productGroup);
    setFilteredProducts(filtered);
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedProduct(null);
  };

  // Build table columns
  const getTableColumns = () => {
    const { allDates } = getAllAvailableData();

    const columns = [
      {
        title: t("name"),
        dataIndex: "productName",
        key: "productName",
        width: isMobile ? 150 : 240,
        fixed: "left",
        defaultSortOrder: "descend",
        sorter: (a, b) => utils.antdTableSorter(a, b, "productName"),
        render: (text, record) => {
          if (!hasEditPermission) {
            return <span>{text}</span>;
          }
          return (
            <a style={{ color: "#880e4f" }} onClick={() => handleProductClick(record)}>
              {text}
            </a>
          );
        },
      },
    ];

    // If there's a specific date range selected, only show columns for dates within that range
    if (dateRange && dateRange.length === 2) {
      const startDate = dayjs(dateRange[0]);
      const endDate = dayjs(dateRange[1]);

      // Find dates within the selected range
      const matchingDates = allDates.filter((d) => {
        const stockDate = d.dateUnix * 1000;
        return dayjs(stockDate).isSameOrAfter(startDate, "day") && dayjs(stockDate).isSameOrBefore(endDate, "day");
      });

      matchingDates.forEach((date, index) => {
        const isEvenColumn = index % 2 === 0;
        const columnStyle = isEvenColumn ? {} : { background: "#f9f9f9" };

        columns.push({
          title: (
            <div style={{ textAlign: "center" }}>
              <div>
                {dayjs(date.dateUnix * 1000)
                  .locale("en")
                  .format("dddd")}
              </div>
              <div>{dayjs(date.dateUnix * 1000).format("DD-MM-YYYY")}</div>
            </div>
          ),
          key: date.id,
          children: [
            {
              title: t("forecast"),
              dataIndex: `${date.id}_projected`,
              key: `${date.id}_projected`,
              width: 120,
              render: (text) => <span>{text}</span>,
              sorter: (a, b) => utils.antdTableSorter(a, b, `${date.id}_projected`),
              onCell: () => ({ style: columnStyle }),
              onHeaderCell: () => ({ style: columnStyle }),
            },
          ],
          onHeaderCell: () => ({ style: columnStyle }),
        });
      });
    }

    return columns;
  };

  const getMatchingDatesCount = () => {
    if (!dateRange || dateRange.length !== 2) return 0;

    const startDate = dayjs(dateRange[0]);
    const endDate = dayjs(dateRange[1]);
    const { allDates } = getAllAvailableData();

    return allDates.filter((d) => {
      const stockDate = d.dateUnix * 1000;
      return dayjs(stockDate).isSameOrAfter(startDate, "day") && dayjs(stockDate).isSameOrBefore(endDate, "day");
    }).length;
  };

  useEffect(() => {
    filterOrdersByDateRange(dateRange);
  }, [orders, dateRange]);

  return (
    <>
      <Card>
        <Flex alignItems="center" justifyContent="between" mobileFlex={true}>
          {!isMobileView ? (
            <Space wrap style={{ marginBottom: 6 }}>
              <Input
                style={{ maxWidth: 120 }}
                value={searchText}
                placeholder={t("search")}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => onSearch(e.target.value)}
              />
              <StyledSelect
                className="w-100"
                style={{ minWidth: 140 }}
                $isFiltered={clickedProductCompany !== "All"}
                onChange={handleShowProductCompany}
                placeholder={t("company")}
                defaultValue={clickedProductCompany !== "null" ? clickedProductCompany : "All"}
              >
                <Option value="All">{t("all_companies")}</Option>
                {PRODUCT_COMPANY_GROUPS.map((group) => (
                  <Option key={group.value} value={group.value}>
                    {group.label}
                  </Option>
                ))}
              </StyledSelect>
              <StyledSelect
                className="w-100"
                style={{ minWidth: 140 }}
                $isFiltered={clickedProductGroup !== "All"}
                onChange={handleShowProductGroup}
                placeholder={t("product_group")}
                defaultValue={clickedProductGroup !== "null" ? clickedProductGroup : "All"}
              >
                <Option value="All">{t("all_groups")}</Option>
                {PRODUCT_GROUPS.map((group) => (
                  <Option key={group.value} value={group.value}>
                    {t(group.label)}
                  </Option>
                ))}
              </StyledSelect>
              <StyledSelect
                className="w-100"
                style={{ minWidth: 140 }}
                $isFiltered={clickedCategory !== "All"}
                onChange={handleShowCategory}
                placeholder={t("category")}
                defaultValue={clickedCategory !== "null" ? clickedCategory : "All"}
              >
                <Option value="All">{t("all_categories")}</Option>
                {!!categories &&
                  categories.map((elm) => (
                    <Option key={elm} value={elm}>
                      {elm}
                    </Option>
                  ))}
              </StyledSelect>
              <Popover
                content={
                  <div style={{ padding: "8px 0" }}>
                    <RangePicker
                      value={tempDateRange}
                      onChange={(dates) => setTempDateRange(dates)}
                      disabledDate={(current) => {
                        const maxFutureDate = dayjs().add(28, "days").endOf("day");
                        return current && current.isAfter(maxFutureDate);
                      }}
                      placeholder={["Start date", "End date"]}
                      style={{ marginBottom: 8 }}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
                      <Button
                        size="small"
                        onClick={() => {
                          setCustomDatePickerVisible(false);
                          setTempDateRange(null);
                        }}
                      >
                        {t("cancel")}
                      </Button>
                      <Button
                        type="primary"
                        size="small"
                        disabled={!tempDateRange || tempDateRange.length !== 2}
                        onClick={() => {
                          if (tempDateRange && tempDateRange.length === 2) {
                            handleDateRangeChange(tempDateRange);
                            setCustomDatePickerVisible(false);
                            setTempDateRange(null);
                          }
                        }}
                      >
                        {t("apply")}
                      </Button>
                    </div>
                  </div>
                }
                title={t("select_custom_date_range")}
                trigger="click"
                open={customDatePickerVisible}
                onOpenChange={(visible) => {
                  setCustomDatePickerVisible(visible);
                  if (visible) {
                    setTempDateRange(dateRange);
                  } else {
                    setTempDateRange(null);
                  }
                }}
              >
                <CustomButton type={activeTimeFilter === "custom" ? "secondary" : "default"}>
                  {activeTimeFilter === "custom" && dateRange
                    ? `${dayjs(dateRange[0]).format("DD/MM")} - ${dayjs(dateRange[1]).format("DD/MM")}`
                    : t("custom_range")}
                </CustomButton>
              </Popover>
              <Tooltip title={t("upcoming_week_tooltip")}>
                <CustomButton
                  onClick={() => applyTimeFilter("week")}
                  type={activeTimeFilter === "week" ? "secondary" : "default"}
                >
                  {t("upcoming_week")}
                </CustomButton>
              </Tooltip>
            </Space>
          ) : (
            <Popover
              content={
                <FilterPopoverContent>
                  <div>
                    <FilterLabel>{t("search")}</FilterLabel>
                    <Input
                      value={searchText}
                      placeholder={t("search")}
                      prefix={<SearchOutlined />}
                      allowClear
                      onChange={(e) => onSearch(e.target.value)}
                      size="large"
                    />
                  </div>
                  <div>
                    <FilterLabel>{t("company")}</FilterLabel>
                    <Select
                      placeholder={t("company")}
                      style={{ width: "100%" }}
                      value={clickedProductCompany !== "null" ? clickedProductCompany : "All"}
                      onChange={handleShowProductCompany}
                      size="large"
                    >
                      <Option value="All">{t("all_companies")}</Option>
                      {PRODUCT_COMPANY_GROUPS.map((group) => (
                        <Option key={group.value} value={group.value}>
                          {group.label}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <FilterLabel>{t("product_group")}</FilterLabel>
                    <Select
                      placeholder={t("product_group")}
                      style={{ width: "100%" }}
                      value={clickedProductGroup !== "null" ? clickedProductGroup : "All"}
                      onChange={handleShowProductGroup}
                      size="large"
                    >
                      <Option value="All">{t("all_groups")}</Option>
                      {PRODUCT_GROUPS.map((group) => (
                        <Option key={group.value} value={group.value}>
                          {t(group.label)}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <FilterLabel>{t("category")}</FilterLabel>
                    <Select
                      placeholder={t("category")}
                      style={{ width: "100%" }}
                      value={clickedCategory !== "null" ? clickedCategory : "All"}
                      onChange={handleShowCategory}
                      size="large"
                    >
                      <Option value="All">{t("all_categories")}</Option>
                      {!!categories &&
                        categories.map((elm) => (
                          <Option key={elm} value={elm}>
                            {elm}
                          </Option>
                        ))}
                    </Select>
                  </div>
                  <div>
                    <FilterLabel>{t("select_custom_date_range")}</FilterLabel>
                    <RangePicker
                      value={dateRange}
                      onChange={(dates) => {
                        if (dates && dates.length === 2) {
                          handleDateRangeChange(dates);
                        }
                      }}
                      disabledDate={(current) => {
                        const maxFutureDate = dayjs().add(28, "days").endOf("day");
                        return current && current.isAfter(maxFutureDate);
                      }}
                      placeholder={["Start date", "End date"]}
                      style={{ width: "100%" }}
                      size="large"
                      popupClassName="single-month-range-picker"
                    />
                  </div>
                  <FilterButtonsContainer>
                    <Button
                      onClick={() => {
                        const weekRange = [dayjs().startOf("day"), dayjs().add(6, "days").endOf("day")];
                        localStorage.removeItem("categoryFilter");
                        localStorage.removeItem("productCompanyFilter");
                        localStorage.removeItem("productGroupFilter");
                        setSearchText("");
                        setClickedProductCompany("All");
                        setClickedProductGroup("All");
                        setClickedCategory("All");
                        setActiveTimeFilter("week");
                        setDateRange(weekRange);
                        checkIfNeedsAdditionalFetch(weekRange);
                        filterOrdersByDateRange(weekRange);
                        if (productList && productList.length > 0) {
                          const filtered = applyFiltersDirectly(productList, "", "All", "All", weekRange, "All");
                          setFilteredProducts(filtered);
                        }
                        setFilterPopoverVisible(false);
                      }}
                      style={{ flex: 1 }}
                    >
                      {t("reset")}
                    </Button>
                    <Button type="primary" onClick={() => setFilterPopoverVisible(false)} style={{ flex: 1 }}>
                      {t("apply")}
                    </Button>
                  </FilterButtonsContainer>
                </FilterPopoverContent>
              }
              title={t("filters")}
              trigger="click"
              open={filterPopoverVisible}
              onOpenChange={setFilterPopoverVisible}
              placement="bottomLeft"
            >
              <Button
                icon={<FilterOutlined />}
                style={{
                  borderColor:
                    clickedProductCompany !== "All" ||
                    clickedProductGroup !== "All" ||
                    clickedCategory !== "All" ||
                    activeTimeFilter === "custom" ||
                    searchText
                      ? "#FF4B33"
                      : "",
                  color:
                    clickedProductCompany !== "All" ||
                    clickedProductGroup !== "All" ||
                    clickedCategory !== "All" ||
                    activeTimeFilter === "custom" ||
                    searchText
                      ? "#FF4B33"
                      : "",
                  marginBottom: 8,
                }}
              />
            </Popover>
          )}
        </Flex>
        {isLoading || productsLoading ? (
          <Loading />
        ) : (
          <div className="table-responsive" ref={tableContainerRef}>
            <ForecastTable
              matchingDatesCount={getMatchingDatesCount()}
              showSorterTooltip={false}
              columns={getTableColumns()}
              dataSource={filteredProducts}
              rowKey="key"
              sticky
              scroll={{ x: "max-content", y: tableY }}
              pagination={false}
              locale={{
                emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("table_no_products")} />,
              }}
            />
          </div>
        )}
      </Card>

      <ProductStockModal visible={modalVisible} onCancel={handleModalClose} product={selectedProduct} />
    </>
  );
};

export default Forecast;

const getTableMaxWidth = (matchingDatesCount) => {
  switch (matchingDatesCount) {
    case 1:
      return "570px";
    case 2:
      return "770px";
    case 3:
      return "970px";
    case 4:
      return "1170px";
    case 5:
      return "1370px";
    case 6:
      return "1570px";
    case 7:
      return "1770px";

    default:
      return "none";
  }
};

const ForecastTable = styled(Table)`
  div.ant-table-body {
    max-height: 65vh !important;
  }

  max-width: ${(props) => getTableMaxWidth(props.matchingDatesCount)};
`;

const CustomButton = ({ type, children, ...props }) => {
  const getButtonStyle = () => {
    if (type === "secondary") {
      return {
        backgroundColor: "#f0f0f0",
        borderColor: "#d9d9d9",
        color: "#595959",
      };
    }
    return {};
  };

  return (
    <Button {...props} style={{ ...getButtonStyle(), ...props.style }}>
      {children}
    </Button>
  );
};

const FilterPopoverContent = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
`;

const FilterButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
  margin-top: 4px;
`;
